import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {SequenceCardScratchpadState} from '../../scratchpad/sequenceCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const deleteRegularOutputsInputSchema = z.object({
  outputIds: z
    .array(z.number())
    .describe('The ids of the regular outputs to delete, at least 1 regular output must remain'),
});

export default function deleteRegularOutputsTool() {
  async function execute(
    params: ToolExecuteParams<typeof deleteRegularOutputsInputSchema, SequenceCardScratchpadState>,
  ) {
    const {outputIds} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<SequenceCardScratchpadState>(
      agentScratchpad,
      NodeType.SEQUENCE,
    );

    const outputsToDelete = validatedScratchpad.card.outputs.filter((output) =>
      outputIds.includes(output.id),
    );

    const hasFallbackInTarget = outputsToDelete.some(
      (output) => output.type === OutputType.Fallback,
    );

    if (hasFallbackInTarget) {
      return toolError(
        'The Fallback output is a built-in system output for unidentified users and cannot be deleted or modified.',
      );
    }

    const hasLastInTarget = outputsToDelete.some((output) => output.type === OutputType.Last);

    if (hasLastInTarget) {
      return toolError(
        'The Last output cannot be directly deleted. It is tied to the repeat cycle configuration. To remove it, use the setRepeatCycle tool with mode "infinite", which will remove the Last output and make the sequence loop without end.',
      );
    }

    const remainingOutputs = validatedScratchpad.card.outputs.filter(
      (output) => !outputIds.includes(output.id),
    );
    const remainingRegularCount = remainingOutputs.filter(
      (output) => output.type === OutputType.Regular,
    ).length;

    if (remainingRegularCount < 1) {
      const currentRegularCount = validatedScratchpad.card.outputs.filter(
        (output) => output.type === OutputType.Regular,
      ).length;

      return toolError(
        `Cannot delete all regular outputs: sequence cards must have at least 1 regular output. You currently have ${currentRegularCount} regular output(s) and are trying to delete ${outputIds.length}.`,
      );
    }

    validatedScratchpad.card.outputs = remainingOutputs;

    return toolResult(`Successfully deleted ${outputIds.length} regular output(s)`);
  }

  return tool<typeof deleteRegularOutputsInputSchema, SequenceCardScratchpadState>({
    name: V2CardAgentToolName.DeleteRegularOutputs,
    displayName: 'Deleting regular outputs',
    description:
      'Delete regular outputs from the sequence card by ID. At least 1 regular output must remain. Cannot delete Fallback or Last outputs directly -- use setRepeatCycle with mode "infinite" to remove the Last output.',
    inputSchema: deleteRegularOutputsInputSchema,
    execute,
  });
}
